import { useMutation, useQuery } from '@tanstack/react-query';
import type { GridApi, IServerSideGetRowsParams } from 'ag-grid-community';
import { AxiosError } from 'axios';
import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useEffect, useMemo } from 'react';

import type {
  QBFilter,
  QBFilterDefinitionsResponseDTO,
  QBFilterOptions
} from '@/components/QueryBuilder';
import {
  mapQBFilterDefinitionsToConfig,
  mapQBGroupByOptions
} from '@/components/QueryBuilder/QueryBuilder.utils';
import type { TimePresetKeyWithField } from '@/components/TimeFilter';
import {
  ASSETS_FILTERS_STORAGE_KEY,
  DEFAULT_PAGE_SIZE
} from '@/constants/common';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient, queryClient } from '@/services/BaseApi.service';
import type { Order } from '@/types/common';

import { selectedOrganizationAtom } from '../Organizations';
import { mapFiltersToRequestParams } from '../Tickets/Tickets.store';
import {
  VULNERABILITIES_HIDE_COUNTS_FIELDS,
  VULNERABILITIES_SEARCH_FIELD_LABELS
} from './VulnerabilityManagement.constants';
import type {
  VmpAssetResponseDTO,
  VulnerabilityType
} from './VulnerabilityManagement.types';
import { replaceUnwantedValues } from './VulnerabilityManagement.utils';

const jotaiSessionStorage = createJSONStorage<SearchFilters>(
  () => sessionStorage
);

export const vmpAssetsFiltersAtom = atomWithStorage<SearchFilters>(
  ASSETS_FILTERS_STORAGE_KEY,
  {},
  jotaiSessionStorage
);

export const vmpWidgetsCollapsedAtom = atomWithStorage<boolean>(
  'vmpWidgetsCollapsed',
  false
);

export const vulnerabilitiesGroupsAtom = atom<
  Record<string, QBFilterOptions[]>
>({});

export const vmpAssetsTablePageAtom = atom<number>(0);

export const vmpRemediationsGridApiAtom = atom<GridApi | null>(null);

export const vmpAffectedAssetsTableRowsAtom = atomWithStorage<number>(
  'VMPAffectedAssetsTableRows',
  DEFAULT_PAGE_SIZE
);

export const vmpRemediatedAssetsTableRowsAtom = atomWithStorage<number>(
  'VMPRemediatedAssetsTableRows',
  DEFAULT_PAGE_SIZE
);

export const vmpTableOrderAssetsAtom = atomWithStorage<Order>(
  'VMPAssetsTableOrder',
  'asc'
);
export const vmpTableOrderByAssetsAtom = atomWithStorage<string>(
  'VMPAssetsTableOrderBy',
  'id'
);

export const vmpVulnerabilitiesAtom = atom<VulnerabilityType[]>([]);

export const vmpAssetsAtom = atom<VmpAssetResponseDTO[]>([]);

export const useFetchVMPAssets = () => {
  const [, setAssets] = useAtom(vmpAssetsAtom);
  const handleError = useHandleError();
  const [organization] = useAtom(selectedOrganizationAtom);

  const queryFn = () => {
    return axiosClient
      .get(`organizations/${organization.label}/vmp/assets`)
      .then((resp) => resp.data);
  };

  const { data, isError, error, refetch, isSuccess, isLoading } = useQuery({
    queryKey: ['vmp/assets', organization],
    queryFn,
    staleTime: 60 * 1000 // 1 minute
  });

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch assets.');
    }
    if (isSuccess) {
      const updatedAssets: VmpAssetResponseDTO[] = replaceUnwantedValues(data);
      setAssets(updatedAssets as VmpAssetResponseDTO[]);
    }
  }, [isError, isSuccess, data]);

  return {
    isLoading,
    refetch
  };
};

export const shieldSyncData = () =>
  axiosClient.post('/organizations/shield_sync');

export const clearVulnerabilitiesSearchCache = () => {
  queryClient.removeQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === '/v2/vulnerabilities/search' ||
      queryKey[0] === '/v2/vulnerabilities/search/cves'
  });
};

export const getVulnerabilitiesServerSideDatasource = (
  organizationLabel: string,
  vulnerabilitiesFilters?: QBFilter[] | null,
  vulnerabilitiesTimeFilters?: TimePresetKeyWithField | null,
  handleError?: (errorResponse: AxiosError, errorMessage: string) => void,
  onLoadingChange?: (isLoading: boolean) => void,
  groupedByVulnerability?: boolean
) => {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      onLoadingChange?.(true);

      try {
        const { startRow, sortModel } = params.request;
        const pageSize = params.api.paginationGetPageSize();
        const pagesToFetch = pageSize * 5;

        // Build query parameters
        const queryParams = new URLSearchParams({
          page: (
            Math.floor((startRow as number) / pagesToFetch) + 1
          ).toString(),
          page_size: pagesToFetch.toString()
        });

        const requestParams = mapFiltersToRequestParams(
          [...(vulnerabilitiesFilters || [])],
          vulnerabilitiesTimeFilters,
          'date_identified',
          'last_changed_at'
        );

        // Add organization filter if it is not the all organizations option
        if (organizationLabel !== 'all' && organizationLabel !== 'noop') {
          requestParams.organization__label = organizationLabel;
        }

        // Add sorting if specified
        if (sortModel && sortModel.length > 0) {
          let { colId } = sortModel[0];
          const { sort } = sortModel[0];
          const sortPrefix = sort === 'asc' ? '' : '-';
          if (colId === 'organization_name') {
            colId = 'organization__name';
          }
          requestParams.order_by = `${sortPrefix}${colId}`;
        }

        if (
          organizationLabel !== 'all' &&
          organizationLabel !== 'noop' &&
          requestParams.order_by === 'organization__name'
        ) {
          delete requestParams.order_by; // Remove primary sorting by organization name when filtering by specific organization
        }

        const urlString = groupedByVulnerability
          ? '/v2/vulnerabilities/search/cves'
          : '/v2/vulnerabilities/search';

        const response = await queryClient.fetchQuery({
          queryKey: [urlString, Object.fromEntries(queryParams), requestParams],
          queryFn: () =>
            axiosClient
              .post(`${urlString}?${queryParams.toString()}`, requestParams)
              .then((resp) => resp.data),
          staleTime: 5 * 60 * 1000
        });

        const result = {
          rowData: response.data || [],
          rowCount: response.pagination.total || 0
        };
        params.success(result);
      } catch (error) {
        console.error('Server-side datasource error:', error);
        params.fail();

        if (handleError) {
          const axiosError =
            error instanceof AxiosError
              ? error
              : new AxiosError(
                  error instanceof Error ? error.message : String(error)
                );

          handleError(axiosError, 'Failure to fetch vulnerabilities.');
        }
      } finally {
        onLoadingChange?.(false);
      }
    }
  };
};

export const useFetchVulnerabilitiesGroupByOptions = (
  groups: string[],
  customFilters?: Record<
    string,
    string | string[] | number | number[] | boolean
  >,
  setGroups?: (groups: Record<string, QBFilterOptions[]>) => void,
  enabled = true
) => {
  const handleError = useHandleError();
  const selectedOrg = useAtomValue(selectedOrganizationAtom);

  const requestParams = useMemo(() => {
    if (customFilters) {
      return { ...customFilters, groups };
    }

    return { groups };
  }, [groups, customFilters, selectedOrg]);

  const { data, isError, error, isLoading, isFetching, isSuccess } = useQuery({
    queryKey: ['/v2/vulnerabilities/group_by', requestParams],
    queryFn: () =>
      axiosClient
        .post(
          `/v2/vulnerabilities/group_by?page=1&page_size=100`,
          requestParams
        )
        .then((resp) => resp.data),
    select: mapQBGroupByOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch group by options for vulnerabilities.'
      );
    } else if (isSuccess) {
      if (setGroups) {
        setGroups(data || []);
      }
    }
  }, [isError, error, handleError, isSuccess, data, setGroups]);

  return { data, isLoading, isFetching };
};

export const useFetchVulnerabilityFilters = () => {
  const handleError = useHandleError();

  const { data, isError, error, isLoading } = useQuery({
    queryKey: ['v2/vulnerabilities/filters'],
    staleTime: Infinity,
    select: (data) =>
      mapQBFilterDefinitionsToConfig(
        data as QBFilterDefinitionsResponseDTO,
        VULNERABILITIES_SEARCH_FIELD_LABELS,
        VULNERABILITIES_HIDE_COUNTS_FIELDS,
        [{ pattern: /\bVmp\b/g, replacement: 'VMP' }]
      )
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch vulnerability filters configuration.'
      );
    }
  }, [isError, error]);

  return { data, isLoading };
};

export const useFetchVMPEExport = () => {
  return useMutation({
    mutationFn: ({
      format,
      vmpeFilters,
      isGroupedByVulnerability
    }: {
      format: 'json' | 'csv';
      vmpeFilters: Record<string, string>;
      isGroupedByVulnerability: boolean;
    }) => {
      const params = new URLSearchParams({
        export_format: format
      });
      const url = isGroupedByVulnerability
        ? '/v2/vulnerabilities/export/group_by_cve'
        : '/v2/vulnerabilities/export';
      return axiosClient.post(url, vmpeFilters || {}, {
        params,
        responseType: 'blob'
      });
    }
  });
};
